/**
 * Blocking modal shown when a reunification is activated at a site the
 * user is authorized to manage. User chooses Join (navigate to the event)
 * or Dismiss (close; same event_id won't re-prompt this session).
 *
 * Drills use the same modal; the title and subheading are relabeled.
 */

import { Box, Dialog, HStack, Portal, Text, VStack } from '@chakra-ui/react';
import { Button } from '@themes/aegix';
import { HiExclamationTriangle, HiAcademicCap } from 'react-icons/hi2';
import type { ReunificationJoinPromptPayload } from '../../contexts/ReunificationJoinPromptContext';

interface Props {
  payload: ReunificationJoinPromptPayload | null;
  onJoin: () => void;
  onDismiss: () => void;
}

function formatStartedAt(iso: string | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString();
}

export function ReunificationJoinPromptDialog({
  payload,
  onJoin,
  onDismiss,
}: Props) {
  const isOpen = payload !== null;
  const isDrill = payload?.is_drill === true;

  const title = isDrill ? 'Reunification drill started' : 'Reunification started';
  const Icon = isDrill ? HiAcademicCap : HiExclamationTriangle;
  const iconColor = isDrill ? 'var(--chakra-colors-blue-500)' : 'var(--chakra-colors-orange-500)';

  const startedAt = formatStartedAt(payload?.started_at);

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(details) => {
        if (!details.open) onDismiss();
      }}
      placement="center"
      size="sm"
      closeOnInteractOutside={false}
      closeOnEscape={false}
      role="alertdialog"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            bgColor="surface.primary"
            borderRadius="lg"
            maxWidth="480px"
            data-e2e="reunification-join-prompt"
          >
            <Dialog.Header
              p="20px 20px 8px 20px"
              display="flex"
              alignItems="center"
              gap={3}
            >
              <Box flexShrink={0} color={iconColor}>
                <Icon size={28} />
              </Box>
              <Dialog.Title fontSize="16px" fontWeight={600} color="text.primary">
                {title}
              </Dialog.Title>
            </Dialog.Header>

            <Dialog.Body p="0 20px 16px 20px">
              <VStack align="stretch" gap={2}>
                {payload?.school_name && (
                  <Text fontSize="sm" color="text.primary" fontWeight={500}>
                    {payload.school_name}
                  </Text>
                )}
                {payload?.incident_type && (
                  <Text fontSize="sm" color="text.secondary">
                    Incident type:{' '}
                    <Text as="span" color="text.primary">
                      {payload.incident_type}
                    </Text>
                  </Text>
                )}
                {startedAt && (
                  <Text fontSize="sm" color="text.secondary">
                    Started: <Text as="span" color="text.primary">{startedAt}</Text>
                  </Text>
                )}
                {payload?.activated_by && (
                  <Text fontSize="sm" color="text.secondary">
                    Activated by:{' '}
                    <Text as="span" color="text.primary">
                      {payload.activated_by}
                    </Text>
                  </Text>
                )}
                <Text fontSize="sm" color="text.secondary" mt={2}>
                  Join the event to manage it in SMS.
                </Text>
              </VStack>
            </Dialog.Body>

            <Box p="0 20px 20px 20px">
              <HStack gap={3} justify="flex-end">
                <Button
                  visual="secondary"
                  onClick={onDismiss}
                  data-e2e="reunification-join-prompt-dismiss"
                >
                  Dismiss
                </Button>
                <Button
                  visual="primary"
                  onClick={onJoin}
                  data-e2e="reunification-join-prompt-join"
                >
                  Join
                </Button>
              </HStack>
            </Box>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
